// Phase 86AZ.60 — Ultra Admin: Müteahhit Adayları (Contractor Candidates)
// =========================================================================
// /ultra-admin/muteahhit/adaylar — table of every contractor candidate that
// has been recommended via the public /muteahhit-oner/:token form. Each
// row shows the candidate's company info + recommender's name (denormalized
// on the candidate row so no join is needed).
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { apiClient } from '../lib/apiClient';
import {
  HardHat, Search, RefreshCw, Trash2, FileText, Mail, Phone,
  User, Tag, Eye, Calendar, MessageSquare, Globe,
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import AdminSidebar from '../components/AdminSidebar';
import PresentationPreviewModal from '../components/PresentationPreviewModal';

const PresentationButton = ({ candId, name, size, onOpen }) => (
  <button
    type="button"
    onClick={() => onOpen({ candId, name, size })}
    className="inline-flex items-center gap-1.5 text-xs text-red-700 hover:text-red-800 hover:underline"
    data-testid={`presentation-link-${candId}`}>
    <FileText className="h-3.5 w-3.5" />
    <span className="truncate max-w-[140px]">{name}</span>
    {size != null && <span className="text-gray-400">({(size / 1024).toFixed(0)} KB)</span>}
  </button>
);

export default function UltraAdminMuteahhitAdaylari() {
  const { language } = useLanguage();
  const tr = language === 'tr';

  const [rows,   setRows]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // Phase 86AZ.60.2 — Presentation preview modal state.
  const [preview, setPreview] = useState(null);  // {candId, name, size} | null

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get('/ultra-admin/contractor-candidates');
      setRows(r.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id, company) => {
    if (!window.confirm(tr ? `"${company}" adayını silmek istediğinize emin misiniz?` : `Delete "${company}"?`)) return;
    try {
      await apiClient.delete(`/ultra-admin/contractor-candidates/${id}`);
      setRows(prev => prev.filter(r => r.id !== id));
      toast.success(tr ? 'Aday silindi.' : 'Candidate deleted.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Silinemedi.');
    }
  };

  const filtered = search
    ? rows.filter(r => {
        const q = search.toLowerCase();
        return (r.company || '').toLowerCase().includes(q)
            || (r.contact_email || '').toLowerCase().includes(q)
            || (r.recommender_name || '').toLowerCase().includes(q)
            || (r.category || '').toLowerCase().includes(q);
      })
    : rows;

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <AdminSidebar />
      <div className="flex-1 overflow-x-hidden">
        <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="muteahhit-adaylar-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="h-12 w-12 rounded-2xl bg-red-100 flex items-center justify-center flex-shrink-0">
            <HardHat className="h-6 w-6 text-red-600" />
          </div>
          <div>
            <h1 className="font-playfair text-2xl text-[#0A3A5A] font-medium">{tr ? 'Tedarikçi Adayları' : 'Supplier Candidates'}</h1>
            <p className="text-sm text-gray-500 mt-0.5">{tr ? 'Önericiler tarafından önerilen tedarikçi firmalar' : 'Supplier firms recommended by your referrers'}</p>
          </div>
        </div>
        <Button onClick={load} variant="outline" className="rounded-full" data-testid="adaylar-refresh">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          {tr ? 'Yenile' : 'Refresh'}
        </Button>
      </div>

      {/* Search */}
      <Card className="border-0 shadow-sm rounded-2xl mb-6">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              data-testid="adaylar-search"
              placeholder={tr ? 'Firma, e-posta, kategori veya öneren ara...' : 'Search by company, email, category or recommender...'}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 rounded-xl text-sm"
            />
          </div>
        </CardContent>
      </Card>

      {/* Stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="bg-white border border-[rgba(10,58,90,0.08)] rounded-2xl p-4">
          <p className="text-[11px] uppercase tracking-wide text-gray-500">{tr ? 'Toplam Aday' : 'Total Candidates'}</p>
          <p className="font-playfair text-2xl text-[#0A3A5A] mt-1">{rows.length}</p>
        </div>
        <div className="bg-white border border-[rgba(10,58,90,0.08)] rounded-2xl p-4">
          <p className="text-[11px] uppercase tracking-wide text-gray-500">{tr ? 'Bekleyen' : 'Pending'}</p>
          <p className="font-playfair text-2xl text-amber-600 mt-1">{rows.filter(r => r.status === 'pending').length}</p>
        </div>
        <div className="bg-white border border-[rgba(10,58,90,0.08)] rounded-2xl p-4">
          <p className="text-[11px] uppercase tracking-wide text-gray-500">{tr ? 'Sunumlu' : 'With Presentation'}</p>
          <p className="font-playfair text-2xl text-red-600 mt-1">{rows.filter(r => r.presentation_file).length}</p>
        </div>
        <div className="bg-white border border-[rgba(10,58,90,0.08)] rounded-2xl p-4">
          <p className="text-[11px] uppercase tracking-wide text-gray-500">{tr ? 'Farklı Öneren' : 'Unique Referrers'}</p>
          <p className="font-playfair text-2xl text-emerald-600 mt-1">{new Set(rows.map(r => r.recommender_id)).size}</p>
        </div>
      </div>

      {/* Table */}
      <Card className="border-0 shadow-sm rounded-2xl overflow-hidden">
        {loading && rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-400">{tr ? 'Yükleniyor...' : 'Loading...'}</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <HardHat className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">{search ? (tr ? 'Aramayla eşleşen kayıt yok.' : 'No matching records.') : (tr ? 'Henüz aday önerilmemiş.' : 'No candidates have been recommended yet.')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500">
                  <th className="px-4 py-3 font-semibold">{tr ? 'Firma' : 'Company'}</th>
                  <th className="px-4 py-3 font-semibold">{tr ? 'İletişim' : 'Contact'}</th>
                  <th className="px-4 py-3 font-semibold">{tr ? 'Kategori' : 'Category'}</th>
                  <th className="px-4 py-3 font-semibold">{tr ? 'Öneren' : 'Recommended By'}</th>
                  <th className="px-4 py-3 font-semibold">{tr ? 'Sunum' : 'Presentation'}</th>
                  <th className="px-4 py-3 font-semibold">{tr ? 'Tarih' : 'Date'}</th>
                  <th className="px-4 py-3 font-semibold"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.id} className="border-b border-gray-100 last:border-0 hover:bg-red-50/30 transition-colors" data-testid={`aday-row-${r.id}`}>
                    <td className="px-4 py-4">
                      <div className="font-semibold text-gray-800">{r.company}</div>
                      {r.note && (
                        <div className="mt-1 text-xs text-gray-500 flex items-start gap-1 max-w-xs">
                          <MessageSquare className="h-3 w-3 mt-0.5 flex-shrink-0 text-gray-400" />
                          <span className="line-clamp-2">{r.note}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      {r.contact_name && (
                        <div className="text-xs text-gray-700 flex items-center gap-1.5 mb-1 font-medium"><User className="h-3 w-3 text-gray-400" /> {r.contact_name}</div>
                      )}
                      <div className="text-xs text-gray-700 flex items-center gap-1.5"><Mail className="h-3 w-3 text-gray-400" /> {r.contact_email}</div>
                      {r.contact_phone && <div className="text-xs text-gray-500 mt-1 flex items-center gap-1.5"><Phone className="h-3 w-3 text-gray-400" /> {r.contact_phone}</div>}
                      {r.website && (
                        <a href={/^https?:\/\//i.test(r.website) ? r.website : `https://${r.website}`}
                           target="_blank" rel="noreferrer"
                           className="text-xs text-red-700 hover:underline mt-1 flex items-center gap-1.5 truncate max-w-[200px]"
                           data-testid={`aday-website-${r.id}`}>
                          <Globe className="h-3 w-3 text-gray-400" /> {r.website}
                        </a>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      {r.category ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-50 text-red-700 border border-red-100"><Tag className="h-3 w-3" /> {r.category}</span>
                      ) : <span className="text-xs text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-1.5 text-xs">
                        <User className="h-3 w-3 text-gray-400" />
                        <span className="font-medium text-gray-700" data-testid={`aday-recommender-${r.id}`}>{r.recommender_name}</span>
                      </div>
                      <div className="text-[11px] text-gray-400 mt-0.5">{r.recommender_email}</div>
                    </td>
                    <td className="px-4 py-4">
                      {r.presentation_file
                        ? <PresentationButton candId={r.id} name={r.presentation_name || r.presentation_file} size={r.presentation_size} onOpen={setPreview} />
                        : <span className="text-xs text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-xs text-gray-500 flex items-center gap-1"><Calendar className="h-3 w-3" />{(r.created_at || '').slice(0, 10)}</div>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(r.id, r.company)} className="text-red-600 hover:bg-red-50 rounded-full h-7 w-7 p-0" data-testid={`aday-delete-${r.id}`} title={tr ? 'Sil' : 'Delete'}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
        </div>
      </div>
      {/* Phase 86AZ.60.2 — Inline presentation preview modal */}
      {preview && (() => {
        const token = localStorage.getItem('cop31_ultra_token');
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/ultra-admin/contractor-candidates/${preview.candId}/presentation${token ? `?token=${encodeURIComponent(token)}` : ''}`;
        return (
          <PresentationPreviewModal
            open={!!preview}
            onClose={() => setPreview(null)}
            fileUrl={url}
            fileName={preview.name}
          />
        );
      })()}
    </div>
  );
}
